
import * as React from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { regions } from "@/types/regions";
import { countries, Country } from "@/types/countries";

interface Airport {
  id: number;
  name: string;
  iata_code: string | null;
  location: string;
}

export function SearchBar() {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [airports, setAirports] = React.useState<Airport[]>([]);
  const [filteredRegions, setFilteredRegions] = React.useState(regions);
  // Change the type to use Country[] instead of the readonly tuple type
  const [filteredCountries, setFilteredCountries] = React.useState<Country[]>([...countries]);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  React.useEffect(() => {
    const fetchAirports = async () => {
      if (search.length === 0) {
        const { data, error } = await supabase
          .from('airports')
          .select('id, name, iata_code, location')
          .limit(10);
        if (!error && data) {
          setAirports(data);
        }
      } else {
        const { data, error } = await supabase
          .from('airports')
          .select('id, name, iata_code, location')
          .or(`name.ilike.%${search}%,location.ilike.%${search}%,iata_code.ilike.${search}%`)
          .limit(10);
        if (!error && data) {
          setAirports(data);
        }
      }

      // Filter regions and countries based on search
      const filteredRegs = regions.filter(region => 
        region.name.toLowerCase().includes(search.toLowerCase()) ||
        region.description.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredRegions(filteredRegs);

      // Filter countries but treat it as a regular array, not a readonly tuple
      const filteredCountrs = [...countries].filter(country => 
        country.name.toLowerCase().includes(search.toLowerCase()) ||
        country.description.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredCountries(filteredCountrs);
    };
    fetchAirports();
  }, [search]);

  const handleSelect = (type: 'airport' | 'region' | 'country', value: string) => {
    if (type === 'airport') {
      const selectedAirport = airports.find(a => a.name === value);
      if (selectedAirport) {
        setValue(selectedAirport.name);
        setOpen(false);
        navigate(`/airport/${selectedAirport.id}`);
      }
    } else if (type === 'region') {
      const selectedRegion = regions.find(r => r.name === value);
      if (selectedRegion) {
        setValue(selectedRegion.name);
        setOpen(false);
        navigate(`/regions/${selectedRegion.id}`);
      }
    } else if (type === 'country') {
      const selectedCountry = countries.find(c => c.name === value);
      if (selectedCountry) {
        setValue(selectedCountry.name);
        setOpen(false);
        navigate(`/countries/${selectedCountry.id}`);
      }
    }
  };

  return (
    <div className="mb-8">
      <div className="flex gap-4 max-w-3xl mx-auto">
        <div className="relative w-full">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={isMobile ? "Search airports, regions & countries..." : "Search airports, regions & countries by name, location, or features..."}
            className="w-full pl-9"
          />
          {open && search && (
            <div className="absolute mt-2 w-full z-50">
              <Command>
                <CommandList className="rounded-lg border bg-popover shadow-md">
                  <CommandEmpty>No results found.</CommandEmpty>
                  
                  {filteredRegions.length > 0 && (
                    <CommandGroup heading="Regions">
                      {filteredRegions.map((region) => (
                        <CommandItem
                          key={region.id}
                          value={region.name}
                          onSelect={() => handleSelect('region', region.name)}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === region.name ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div>
                            <div className="flex items-center">
                              <span>{region.name}</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {region.description}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {filteredCountries.length > 0 && (
                    <CommandGroup heading="Countries">
                      {filteredCountries.map((country) => (
                        <CommandItem
                          key={country.id}
                          value={country.name}
                          onSelect={() => handleSelect('country', country.name)}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === country.name ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div>
                            <div className="flex items-center">
                              <span>{country.name}</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {country.description}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {airports.length > 0 && (
                    <CommandGroup heading="Airports">
                      {airports.map((airport) => (
                        <CommandItem
                          key={airport.id}
                          value={airport.name}
                          onSelect={() => handleSelect('airport', airport.name)}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === airport.name ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div>
                            <div className="flex items-center">
                              <span>{airport.name}</span>
                              {airport.iata_code && (
                                <span className="ml-2 rounded-md bg-muted px-2 text-sm">
                                  {airport.iata_code}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {airport.location}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
